import { authApi } from 'api';

/**
 * Get registration statistics for a meet
 * @param {string} meetId - The meet ID
 * @returns {Promise} - The response from the API
 */
export const getMeetRegistrationStatsByMeetId = async (meetId) => {
  try {
    const response = await authApi.get(`/MeetRegistration/${meetId}/stats`);
    return response;
  } catch (err) {
    throw err;
  }
};

// --- Self-service (private) meet registration (§6.10) ---
// The caller manages THEIR OWN athletes' registrations for a multi-club meet (Visitor+).
export const getMyMeetRegistrations = async (meetId) => {
  return await authApi.get(`/meet-self-registration/${meetId}/my-registrations`);
};

export const selfRegisterAthlete = async (meetId, payload) => {
  return await authApi.post(`/meet-self-registration/${meetId}/register`, payload);
};

export const selfWithdrawAthlete = async (meetId, athleteProfileId) => {
  return await authApi.delete(`/meet-self-registration/${meetId}/my-athletes/${athleteProfileId}`);
};

export const getMeetRegistrationByMeetId = async (
  meetId,
  includeEvents = false
) => {
  try {
    const response = await authApi.get(
      `/MeetRegistration/${meetId}?includeEvents=${includeEvents}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

export const getExportMeetRegistration = async (meetId) => {
  try {
    const response = await authApi.get(`/MeetRegistration/${meetId}/export-csv`, {
      responseType: 'blob',
    });
    return response;
  } catch (err) {
    throw err;
  }
}

export const getExportAthleteEvents = async (meetId) => {
  try {
    const response = await authApi.get(`/MeetRegistration/${meetId}/export-event-matrix-csv`, {
      responseType: 'blob',
    });
    return response;
  } catch (err) {
    throw err;
  }
}

/**
 * Get all athletes participating in a meet
 * This will be Registered athletes for multiclub meets
 * or Program athletes for single club meets gets all athletes regardless of event types
 * @param {string} meetId - The meet ID
 * @returns {Promise} - The response from the API containing all registered athletes
 */
export const getMeetAthletes = async (meetId) => {
  try {
    const response = await authApi.get(`/MeetRegistration/${meetId}/all-athletes`);
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get available events for an athlete based on age and gender
 * @param {string} meetId - The meet ID
 * @param {number} age - The athlete's age group
 * @param {{ age?: number, gender: string, birthDate?: string }} opts - Pass age (for an athlete already in the club)
 *        OR birthDate ('yyyy-MM-dd', for a new/linked athlete — the server computes the age group). gender is 'M'/'F'.
 * @returns {Promise} - { events: [{eventCode, displayName}], maxEvents: number|null, ageGroup: string }
 *        (maxEvents = per-age-group cap, null = unlimited; ageGroup = server-computed competing age group label, e.g. "U11")
 */
export const getAthleteAvailableEvents = async (meetId, { age, gender, birthDate } = {}) => {
  const params = new URLSearchParams();
  if (age !== undefined && age !== null && age !== '') params.append('age', age);
  if (gender) params.append('gender', gender);
  if (birthDate) params.append('birthDate', birthDate);
  return await authApi.get(
    `/MeetRegistration/${meetId}/athlete-events?${params.toString()}`
  );
};

/**
 * Search the host club's athletes (all membership types) for the manual registration picker.
 * @param {string} meetId
 * @param {string} query - name or club athlete number
 * @returns {Promise} - { athletes: [{ athleteID, firstName, lastName, birthDate, gender, ageGroup,
 *   clubAthleteNumber, membershipType, externalClubName, alreadyRegistered }] }
 */
export const searchClubAthletes = async (meetId, query) => {
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  return await authApi.get(
    `/MeetRegistration/${meetId}/athlete-search?${params.toString()}`
  );
};

/**
 * Register a non-club athlete for a multi-club meet, minting a Visitor — either link an existing platform athlete
 * (set existingAthleteProfileId, with contactEmail = that athlete's primary contact email, which the server requires)
 * or create a new one (provide athlete + primary-contact fields).
 * @param {string} meetId
 * @param {{ existingAthleteProfileId?: string, firstName?: string, lastName?: string, birthDate?: string,
 *   gender?: string, contactEmail?: string, contactFirstName?: string, contactLastName?: string,
 *   clubAthleteNumber: number, externalClubName?: string, eventCodes: number[] }} payload
 */
export const registerVisitorForMeet = async (meetId, payload) => {
  return await authApi.post(
    `/MeetRegistration/${meetId}/register-visitor`,
    payload
  );
};

/**
 * Get eligible athletes not yet registered for a meet
 * Returns athletes from the host club who can participate but have not yet registered.
 * Returns both genders - client should filter by gender if needed.
 * @param {string} meetId - The meet ID
 * @param {number} ageGroup - Age group (e.g., 10, 12, 14)
 * @param {string} memberType - Filter by membership type ('Full', 'Guest', or null for all)
 * @returns {Promise} - Array of eligible athletes
 */
export const getEligibleAthletes = async (meetId, ageGroup, memberType = null) => {
  try {
    const params = new URLSearchParams();
    params.append('ageGroup', ageGroup);
    if (memberType) params.append('memberType', memberType);

    const response = await authApi.get(
      `/MeetRegistration/${meetId}/eligible-athletes?${params.toString()}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get the host club name + distinct representing clubs already entered for this meet (for the club picker).
 * @param {string} meetId - The meet ID
 * @returns {Promise} - { hostClubName: string, clubs: string[] }
 */
export const getRepresentingClubs = async (meetId) => {
  return await authApi.get(`/MeetRegistration/${meetId}/representing-clubs`);
};

/**
 * Search this club's athletes (Members, Guests, Visitors) by name or exact bib number, for the registration picker.
 * @param {string} meetId - The meet ID
 * @param {string} [query] - Name fragment or exact club number; empty returns the roster (up to the server limit).
 * @returns {Promise} - { athletes: [{ athleteID, firstName, lastName, birthDate, gender, ageGroup,
 *   clubAthleteNumber, membershipType, externalClubName, alreadyRegistered }] }
 */
export const searchClubAthletesForMeet = async (meetId, query = '') => {
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  return await authApi.get(
    `/MeetRegistration/${meetId}/athlete-search?${params.toString()}`
  );
};

/**
 * Create or update an athlete's event registrations (upsert)
 * @param {string} meetId - The meet ID
 * @param {string} athleteId - The athlete ID
 * @param {Array<number>} eventCodes - Array of event codes to register for
 * @param {{ updateRepresentingClub?: boolean, externalClubName?: string|null, editIdentity?: boolean,
 *   firstName?: string, lastName?: string, birthDate?: string, gender?: string, clubAthleteNumber?: number }} [opts] -
 *   updateRepresentingClub sets the representing club only (guest-class). editIdentity (Admin, guest-class) also updates
 *   name/DOB/gender/bib/club — a full edit of the registration. Both are no-ops for full members.
 * @returns {Promise} - The response from the API (includes isNewRegistration flag)
 */
export const updateAthleteEvents = async (meetId, athleteId, eventCodes, opts = {}) => {
  try {
    const body = { athleteId, eventCodes };
    if (opts.updateRepresentingClub) {
      body.updateRepresentingClub = true;
      body.externalClubName = opts.externalClubName ?? null;
    }
    if (opts.editIdentity) {
      body.editIdentity = true;
      body.firstName = opts.firstName;
      body.lastName = opts.lastName;
      body.birthDate = opts.birthDate;
      body.gender = opts.gender;
      body.clubAthleteNumber = opts.clubAthleteNumber;
      body.externalClubName = opts.externalClubName ?? null;
    }
    const response = await authApi.put(
      `/MeetRegistration/${meetId}/athlete-events`,
      body
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Delete an athlete's registration from a meet
 * @param {string} meetId - The meet ID
 * @param {string} athleteId - The athlete ID to unregister
 * @returns {Promise} - The response from the API
 */
export const deleteAthleteFromMeet = async (meetId, athleteId) => {
  try {
    const response = await authApi.delete(
      `/MeetRegistration/${meetId}/athlete/${athleteId}`
    );
    return response;
  } catch (err) {
    throw err;
  }
};

/**
 * Get version numbers for a meet (registrations, program, start lists, results)
 * @param {string} meetId - The meet ID
 * @returns {Promise} - The response containing version numbers
 */
export const getMeetVersions = async (meetId) => {
  try {
    const response = await authApi.get(`/MeetRegistration/${meetId}/versions`);
    return response;
  } catch (err) {
    throw err;
  }
};